import {
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile
} from 'firebase/auth';
import { get } from 'lodash';
import { useEffect, useState } from 'react';
import initializeFirebase from '../firebase/firebase.init';

initializeFirebase();

const useFirebase = () => {
  const [user, setUser] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const [isReset, setIsReset] = useState(false);

  const auth = getAuth();
  const googleProvider = new GoogleAuthProvider();

  /**
   * register user with email and password
   */
  const registerUser = (email, password, name, navigate, location) => {
    setIsLoading(true);
    createUserWithEmailAndPassword(auth, email, password)
      .then(() => {
        setAuthError('');
        // setting user for instant show of displayName
        const newUser = { email, displayName: name };
        setUser(newUser);

        // Updating user name
        updateProfile(auth.currentUser, {
          displayName: name,
        })
          .then(() => { })
          .catch(() => { });

        // redirect user
        const destination = location?.state?.from?.pathname || '/';
        navigate(destination, { replace: true });
      })
      .catch((error) => {
        setAuthError(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * login user with email and password
   */
  const loginUser = (email, password, navigate, location) => {
    setIsLoading(true);
    signInWithEmailAndPassword(auth, email, password)
      .then(() => {
        const destinantion = '/';
        navigate(destinantion, { replace: true });
        setAuthError('');
      })
      .catch((error) => {
        switch (error.code) {
          case 'auth/wrong-password':
            setAuthError('Incorrect password');
            break;
          case 'auth/user-not-found':
            setAuthError('User not found');
            break;
          default:
            setAuthError(error.message);
            break;
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * user logout
   */
  const logOut = () => {
    setIsLoading(true);
    signOut(auth)
      .then(() => {
        setUser({});
      })
      .catch((error) => {
        setAuthError(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Signin with google
   */
  const googleRegister = (navigate, location) => {
    setIsLoading(true);
    signInWithPopup(auth, googleProvider)
      .then((result) => {
        const destinantion = location?.state?.from?.pathname || '/';
        navigate(destinantion, { replace: true });
        setAuthError('');
      })
      .catch((error) => {
        setAuthError(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * reset password
   */
  const resetPass = (email) => {
    setIsLoading(true);
    sendPasswordResetEmail(auth, email)
      .then(() => {
        setIsReset(true);
      })
      .catch((error) => {
        setAuthError(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * user observer
   */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setIsLoading(true);
      if (user) {
          setUser(user);
          // create session cookies
          document.cookie =
              '__session=' + get(user, 'accessToken', '') + ';max-age=3600';
      } else {
        setUser({});
      }
      setIsLoading(false);
    });

    return () => unsub;
  }, [auth]);

  return {
    user,
    isLoading,
    authError,
    isReset,
    registerUser,
    loginUser,
    logOut,
    googleRegister,
    resetPass,
  };
};

export default useFirebase;
